/**
 * CalendarIntegration — Settings → Integrations → Google Calendar
 * Route: /app/settings/calendar
 *
 * Per-doctor Google connect / disconnect, sync-direction pill picker,
 * privacy modes for pushed + imported events, calendar selector,
 * manual sync button, and live status (last-sync / last-error).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Calendar as CalIcon, RefreshCw, ExternalLink, ShieldCheck,
  Loader2, Check, X, AlertTriangle, Lock, Info,
} from "lucide-react";

const DIRECTION_OPTIONS = [
  { id: "two_way",          title: "Two-way sync",         desc: "Both calendars stay perfectly in sync." },
  { id: "viyora_to_google", title: "Viyora → Google",      desc: "Only export appointments to Google." },
  { id: "google_to_viyora", title: "Google → Viyora",      desc: "Only import busy time from Google." },
  { id: "disabled",         title: "Disable sync",         desc: "Keep connected but pause all sync." },
];

const PRIVACY_OPTIONS = [
  { id: "hide_name",  title: "Dental Appointment",         desc: "Patient name hidden (recommended)." },
  { id: "first_name", title: "Dental — First name only",   desc: "First name in event title." },
  { id: "full_name",  title: "Dental — Full name",         desc: "Full patient name in event title." },
];

const IMPORT_PRIVACY_OPTIONS = [
  { id: "show_title",    title: "Show event title",  desc: "e.g. \"Conference\"." },
  { id: "busy_only",     title: "Show busy",         desc: "Block the slot without title." },
  { id: "hide_details",  title: "Hide details",      desc: "Block silently — nothing visible." },
];

export default function CalendarIntegration() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const popupRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/google-calendar/status");
      setCfg(data);
    } catch (e) { toast.error(apiError(e, "Failed to load")); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Listen for the popup callback message + refresh state on success.
  useEffect(() => {
    const handler = (ev) => {
      if (ev?.data?.type !== "google-calendar-oauth") return;
      const p = ev.data.payload || {};
      if (p.ok) {
        // Callback now returns the initial-pull stats. When present, we
        // surface the exact count so the user has confidence the sync ran.
        // Also broadcast a global `viyora:gcal-synced` event so any other
        // open Viyora screen (Calendar, Reception) refetches its own view
        // without needing a page reload — that's the "instant appearance"
        // guarantee this bug ask targets.
        if (typeof p.imported === "number") {
          toast.success(`Google Calendar connected · ${p.imported} event${p.imported === 1 ? "" : "s"} imported`);
        } else {
          toast.success("Google Calendar connected");
        }
        if (p.pull_error) toast.error(`Initial sync warning: ${p.pull_error}`);
        window.dispatchEvent(new CustomEvent("viyora:gcal-synced", {
          detail: { source: "oauth_callback", imported: p.imported || 0 },
        }));
        load();
      } else {
        toast.error(p.error || "Connection failed");
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [load]);

  const startConnect = async ({ forceReconnect = false } = {}) => {
    setBusy(true);
    try {
      const { data } = await api.get("/oauth/google-calendar/connect", {
        params: forceReconnect ? { force_reconnect: true } : {},
      });
      const w = 480, h = 640;
      const y = window.top.outerHeight / 2 + window.top.screenY - h / 2;
      const x = window.top.outerWidth  / 2 + window.top.screenX - w / 2;
      popupRef.current = window.open(
        data.authorization_url,
        "google-calendar-oauth",
        `toolbar=no,menubar=no,width=${w},height=${h},top=${y},left=${x}`,
      );
      if (!popupRef.current) toast.error("Popup blocked — please allow pop-ups for Viyora");
    } catch (e) {
      toast.error(apiError(e, "Could not start OAuth"));
    } finally { setBusy(false); }
  };

  const reconnect = async () => {
    if (!confirm(
      "Reconnect Google Calendar? This revokes the current authorization at Google and starts a fresh consent — required after scope changes.",
    )) return;
    await startConnect({ forceReconnect: true });
  };

  const disconnect = async () => {
    if (!confirm("Disconnect Google Calendar? Existing Viyora appointments will stay but new ones stop syncing.")) return;
    try {
      await api.post("/oauth/google-calendar/disconnect");
      toast.success("Disconnected");
      load();
    } catch (e) { toast.error(apiError(e, "Failed")); }
  };

  const updateCfg = async (patch) => {
    try {
      setBusy(true);
      const { data } = await api.patch("/google-calendar/config", patch);
      setCfg((c) => ({ ...(c || {}), ...(data || {}) }));
      toast.success("Saved");
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally { setBusy(false); }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/google-calendar/sync-now");
      toast.success(`Synced · ${data.imported} imported`);
      // Broadcast so the Calendar page (or any other open screen) refetches
      // its events immediately — no page reload needed.
      window.dispatchEvent(new CustomEvent("viyora:gcal-synced", {
        detail: { source: "manual_sync", imported: data.imported || 0 },
      }));
      load();
    } catch (e) {
      toast.error(apiError(e, "Sync failed"));
    } finally { setBusy(false); }
  };

  const lastSyncFmt = useMemo(() => {
    if (!cfg?.last_sync) return "Never";
    try { return new Date(cfg.last_sync).toLocaleString("en-IN"); } catch { return cfg.last_sync; }
  }, [cfg?.last_sync]);
  const nextSyncFmt = useMemo(() => {
    if (!cfg?.next_sync) return "—";
    try { return new Date(cfg.next_sync).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false }); } catch { return cfg.next_sync; }
  }, [cfg?.next_sync]);

  if (!cfg) {
    return <div className="p-10 text-ink-soft"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="calendar-integration-page">
      <div>
        <span className="overline text-ink-soft">Settings · Integrations</span>
        <h1 className="mt-2 font-display text-4xl tracking-tight flex items-center gap-3">
          <CalIcon className="w-8 h-8 text-ink" /> Calendar sync
        </h1>
        <p className="mt-2 text-sm text-ink-mid max-w-2xl">
          Keep your Viyora appointments and personal Google Calendar in lockstep — never get
          double-booked, and never let a patient book a slot when you're away.
        </p>
      </div>

      {!cfg.oauth_configured && (
        <div className="rounded-2xl bg-[#FFF4E5] border border-[#F2C494] px-5 py-4 flex items-start gap-3"
             data-testid="oauth-not-configured">
          <AlertTriangle className="w-5 h-5 text-[#8A4B00] shrink-0 mt-0.5" />
          <div className="text-sm text-[#8A4B00]">
            <b>Google Calendar OAuth is not configured on this server.</b>
            <div className="text-xs mt-1 text-[#8A4B00]/80">
              Set <code className="font-mono">GOOGLE_CALENDAR_CLIENT_ID</code> and{" "}
              <code className="font-mono">GOOGLE_CALENDAR_CLIENT_SECRET</code> in the backend
              <code className="font-mono"> .env</code>. Ask your admin.
            </div>
          </div>
        </div>
      )}

      {/* Connection card */}
      <section className="rounded-3xl bg-white/85 border border-[#E7E1D3] p-6 shadow-sm"
               data-testid="connection-card">
        {cfg.connected ? (
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <GoogleLogo />
              <div>
                <div className="flex items-center gap-2">
                  <div className="font-display text-lg text-ink" data-testid="connected-email">
                    {cfg.email || "Google Calendar"}
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#E8F0EB] text-[#2D6A4F]"
                        data-testid="connected-badge">
                    <Check className="w-3 h-3" /> Connected
                  </span>
                </div>
                <div className="text-xs text-ink-mid mt-1">
                  Calendar: <b>{cfg.calendar_name || "Primary"}</b>
                  <span className="mx-2 text-ink-disabled">·</span>
                  Last sync: {lastSyncFmt}
                  <span className="mx-2 text-ink-disabled">·</span>
                  Next: {nextSyncFmt}
                </div>
                {cfg.last_error && (
                  <div className="text-xs text-[#B94A48] mt-1 max-w-md truncate"
                       data-testid="last-error">
                    <AlertTriangle className="inline w-3 h-3 mr-1" />
                    {cfg.last_error}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="btn-sync-now" onClick={syncNow} disabled={busy}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-pine/20 text-sm text-ink hover:bg-linen">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Sync now
              </button>
              <button data-testid="btn-reconnect-google" onClick={reconnect} disabled={busy}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-pine/30 text-sm text-pine hover:bg-linen"
                      title="Revoke at Google and start a fresh consent — required after we change requested scopes.">
                <RefreshCw className="w-3.5 h-3.5" /> Reconnect
              </button>
              <button data-testid="btn-disconnect" onClick={disconnect}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#F2D2BC] text-sm text-[#B94A48] hover:bg-[#FDF3ED]">
                <X className="w-3.5 h-3.5" /> Disconnect
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <GoogleLogo />
              <div>
                <div className="font-display text-lg text-ink">Connect your Google Calendar</div>
                <div className="text-xs text-ink-mid mt-1 max-w-md">
                  We use Google's OAuth 2.0. Refresh tokens are encrypted at rest.
                  You can disconnect any time.
                </div>
              </div>
            </div>
            <button
              data-testid="btn-connect-google"
              onClick={() => startConnect()}
              disabled={busy || !cfg.oauth_configured}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-pine text-linen text-sm font-medium hover:bg-[#264A3B] disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
              Connect Google
            </button>
          </div>
        )}
      </section>

      {cfg.connected && (
        <>
          {/* Sync direction */}
          <section className="rounded-3xl bg-white/85 border border-[#E7E1D3] p-6 shadow-sm"
                   data-testid="direction-card">
            <div className="overline text-ink-soft mb-3">Sync direction</div>
            <div className="grid md:grid-cols-2 gap-3">
              {DIRECTION_OPTIONS.map((opt) => {
                const active = cfg.sync_direction === opt.id;
                return (
                  <button key={opt.id}
                          data-testid={`dir-${opt.id}`}
                          onClick={() => updateCfg({ sync_direction: opt.id })}
                          className={`text-left p-4 rounded-2xl border-2 transition-all ${
                            active ? "border-pine bg-linen" : "border-[#E8EAE9] hover:border-pine/40"
                          }`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-display text-base text-ink">{opt.title}</div>
                      {active && <span className="text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest bg-pine text-linen">Active</span>}
                    </div>
                    <div className="text-xs text-ink-mid mt-1">{opt.desc}</div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Privacy */}
          <section className="rounded-3xl bg-white/85 border border-[#E7E1D3] p-6 shadow-sm"
                   data-testid="privacy-card">
            <div className="flex items-center gap-2 mb-3">
              <Lock className="w-4 h-4 text-ink" />
              <div className="overline text-ink-soft">Privacy — what appears in Google Calendar</div>
            </div>
            <div className="grid md:grid-cols-3 gap-3 mb-6">
              {PRIVACY_OPTIONS.map((opt) => {
                const active = cfg.privacy_mode === opt.id;
                return (
                  <button key={opt.id}
                          data-testid={`priv-${opt.id}`}
                          onClick={() => updateCfg({ privacy_mode: opt.id })}
                          className={`text-left p-4 rounded-2xl border-2 transition-all ${
                            active ? "border-pine bg-linen" : "border-[#E8EAE9] hover:border-pine/40"
                          }`}>
                    <div className="font-medium text-ink">{opt.title}</div>
                    <div className="text-xs text-ink-mid mt-1">{opt.desc}</div>
                  </button>
                );
              })}
            </div>

            <div className="overline text-ink-soft mb-3">Imported Google events on your Viyora calendar</div>
            <div className="grid md:grid-cols-3 gap-3">
              {IMPORT_PRIVACY_OPTIONS.map((opt) => {
                const active = cfg.import_privacy === opt.id;
                return (
                  <button key={opt.id}
                          data-testid={`import-priv-${opt.id}`}
                          onClick={() => updateCfg({ import_privacy: opt.id })}
                          className={`text-left p-4 rounded-2xl border-2 transition-all ${
                            active ? "border-pine bg-linen" : "border-[#E8EAE9] hover:border-pine/40"
                          }`}>
                    <div className="font-medium text-ink">{opt.title}</div>
                    <div className="text-xs text-ink-mid mt-1">{opt.desc}</div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Target calendar */}
          <section className="rounded-3xl bg-white/85 border border-[#E7E1D3] p-6 shadow-sm"
                   data-testid="calendar-select-card">
            <div className="overline text-ink-soft mb-3">Target Google calendar</div>
            <select
              data-testid="calendar-select"
              value={cfg.calendar_id || "primary"}
              onChange={(e) => {
                const sel = (cfg.calendars || []).find((c) => c.id === e.target.value);
                updateCfg({
                  calendar_id: e.target.value,
                  calendar_name: sel?.name || e.target.value,
                });
              }}
              className="w-full max-w-md rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm bg-white">
              <option value="primary">Primary calendar</option>
              {(cfg.calendars || []).filter((c) => c.id !== "primary").map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <div className="text-xs text-ink-soft mt-2 flex items-center gap-1">
              <Info className="w-3 h-3" /> Choose a calendar dedicated to work if you have multiple clinics.
            </div>
          </section>
        </>
      )}

      <div className="rounded-2xl bg-linen/70 border border-[#E7E1D3] p-4 flex items-center gap-2 text-xs text-ink-mid"
           data-testid="security-note">
        <ShieldCheck className="w-4 h-4 text-ink" />
        Refresh tokens are Fernet-encrypted before storage. Google OAuth 2.0 — we never see your password.
      </div>
    </div>
  );
}

function GoogleLogo() {
  return (
    <svg viewBox="0 0 48 48" width="40" height="40" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}
